import Logger from '@tools/logger/logger';

export default class GoogleMapCustom {
  constructor(el, options) {
    this.map = null;
    this.el = el;
    this.mapOptions = {};
    this.globalOptions = {};
    this.transitLayer = null;

    this._parseOptions(options);
  }

  /**
   * Parse user defined options and set Google maps options
   * @param {object} userOptions options
   */
  _parseOptions(userOptions) {
    this.mapOptions = {
      zoom: 14,
      maxZoom: 16,
      options: {
        streetViewControl: false,
        mapTypeControl: false,
        scrollwheel: false,
      },
      disableDefaultUI: true,
      styles: [
        {
          featureType: 'transit.station.bus',
          stylers: [
            { visibility: 'off' },
          ],
        },
        {
          featureType: 'poi',
          stylers: [
            { visibility: 'off' },
          ],
        },
      ],
    };

    let options = userOptions;

    // ensure parameters are correct
    if (options === undefined) {
      options = {};
    }
    if (options.map === undefined) {
      options.map = {};
    }
    if (options.circle === undefined) {
      options.circle = {};
    }
    // Toggle fullscreen control
    this.mapOptions.fullscreenControl = (options.map.fullscreenControl !== undefined)
      ? options.map.fullscreenControl : this._showFullScreenFromDom();

    // Define map center point
    this.mapOptions.center = (options.map.center !== undefined)
      ? options.map.center : this._getCoordinatesFromDom();

    // Define zoom position control
    if (options.map.zoomPosition !== undefined) {
      this.mapOptions.zoomControlOptions = {
        position: this._getZoomPositionFromName(options.map.zoomPosition),
      };
    } else {
      this.mapOptions.zoomControlOptions = {
        position: this._getZoomPositionFromDom(),
      };
    }

    // Toggle display of circle
    this.globalOptions.showCircle = (options.circle.show === undefined)
      ? this._showCircleLocationFromDom() : options.circle.show;
  }

  /**
   * Translate textual zoom position into gmaps const
   * @param {string} positionName
   * @return {int} position name according to google consts
   */
  _getZoomPositionFromName(positionName) { // eslint-disable-line class-methods-use-this
    switch (positionName) {
    case 'top-left':
      return google.maps.ControlPosition.TOP_LEFT; // eslint-disable-line no-undef
    case 'top-right':
      return google.maps.ControlPosition.TOP_RIGHT; // eslint-disable-line no-undef
    case 'bottom-left':
      return google.maps.ControlPosition.BOTTOM_LEFT; // eslint-disable-line no-undef
    case 'bottom-right':
      return google.maps.ControlPosition.BOTTOM_RIGHT; // eslint-disable-line no-undef
    default:
      return google.maps.ControlPosition.BOTTOM_RIGHT; // eslint-disable-line no-undef
    }
  }

  /**
   * Get where to display the zoom buttons (top-right or bottom-right)
   * @returns Position of the zoom buttons (either top-right or bottom-right)
   */
  _getZoomPositionFromDom() { // eslint-disable-line class-methods-use-this
    const zoomPosition = document.querySelector('#zoomPosition');
    if (!zoomPosition) {
      return google.maps.ControlPosition.RIGHT_BOTTOM; // eslint-disable-line no-undef
    }

    return this._getZoomPositionFromName(zoomPosition.value);
  }

  /**
   * Get address location
   * @returns {{lat: number, lng: number}}
   */
  _getCoordinatesFromDom() { // eslint-disable-line class-methods-use-this
    const latElement = document.querySelector('#address-lat');
    const lngElement = document.querySelector('#address-lng');

    if (!(latElement && lngElement)) {
      Logger.error('Missing latitude & longitude coordinates, to init google map');
      return false;
    }

    const lat = parseFloat(latElement.value);
    const lng = parseFloat(lngElement.value);

    return { lat, lng };
  }

  /**
   * Show circle for the map center
   * @returns {boolean} whether to display the center of the map or not
   */
  _showCircleLocationFromDom() { // eslint-disable-line class-methods-use-this
    const showCircle = document.querySelector('#showCircleLocation');
    if (!showCircle) {
      return true;
    }
    return showCircle.value === 'on';
  }

  /**
   * Show the fullScreen controls
   * @returns {boolean} whether to display the fullScreen icon or not
   */
  _showFullScreenFromDom() { // eslint-disable-line class-methods-use-this
    const fullscreen = document.querySelector('#fullScreen');
    if (!fullscreen) {
      return true;
    }
    return fullscreen.value !== 'off';
  }

  /**
   * Display green Circle on map
   */
  _initCircleLocation() {
    const config = {
      strokeColor: '#3C46DC',
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: '#3C46DC',
      fillOpacity: 0.2,
      map: this.map,
      center: this.mapOptions.center,
      radius: 500,
    };
    const cityCircle = new google.maps.Circle(config);// eslint-disable-line no-undef, no-unused-vars, max-len
  }

  /**
   * Display transportation network on map
   */
  _initTransitNetwork() {
    this.transitLayer = new google.maps.TransitLayer();// eslint-disable-line no-undef
    this.transitLayer.setMap(this.map);
  }

  /**
   * Add responsive handler for google map
   */
  _responsiveHandler() {
    google.maps.event.addDomListener(window, 'resize', () => { // eslint-disable-line no-undef
      const center = this.map.getCenter();
      google.maps.event.trigger(this.map, 'resize');// eslint-disable-line no-undef
      this.map.setCenter(center);
    });
  }

  /**
   * Add custom zoom controls on a map
   */
  _customizeZoomControls(position) {
    const zoomControlsWrapper = document.createElement('div');
    zoomControlsWrapper.className = 'google-map-zoom-controls';

    const zoomPlusButton = document.createElement('div');
    zoomPlusButton.className = 'google-map-zoom-controls__plus';
    zoomControlsWrapper.appendChild(zoomPlusButton);

    const zoomMinusButton = document.createElement('div');
    zoomMinusButton.className = 'google-map-zoom-controls__minus';
    zoomControlsWrapper.appendChild(zoomMinusButton);

    const { map } = this;

    google.maps.event.addDomListener(zoomPlusButton, 'click', () => { // eslint-disable-line no-undef
      map.setZoom(map.getZoom() + 1);
    });

    google.maps.event.addDomListener(zoomMinusButton, 'click', () => { // eslint-disable-line no-undef
      map.setZoom(map.getZoom() - 1);
    });

    zoomControlsWrapper.index = 1;
    this.map.controls[position].push(zoomControlsWrapper);
  }

  /**
   * Display google map
   */
  _initMap() {
    if (this.mapOptions.center !== undefined) {
      this.map = new google.maps.Map( // eslint-disable-line no-undef
        this.el,
        this.mapOptions,
      );

      this._customizeZoomControls(this.mapOptions.zoomControlOptions.position);

      if (this.globalOptions.showCircle) {
        this._initCircleLocation();
      }
      this._initTransitNetwork();
      this._responsiveHandler();
      Logger.info('Google map is displayed');
    }
  }

  /**
   * Get api key
   * @returns {string}
   * @private
   */
  _getApiKey() { // eslint-disable-line class-methods-use-this
    return window.GOOGLE_API_KEY;
  }

  /**
   * Get static map url
   *
   * @see https://developers.google.com/maps/documentation/maps-static/dev-guide
   * @see https://stackoverflow.com/questions/7316963/drawing-a-circle-google-static-maps/35660617#35660617
   * @returns {string}
   * @private
   */
  _getStaticMapUrl(isSmall = true) {
    let url = 'https://maps.googleapis.com/maps/api/staticmap?center=';
    if (this.mapOptions.center === undefined) {
      Logger.error('Missing coordinates');
      return '';
    }

    const size = isSmall ? '364x320' : '640x320';

    url += `&size=${size}&scale=${window.devicePixelRatio}`;
    url += `${this.mapOptions.center.lat},${this.mapOptions.center.lng}`;
    url += `&zoom=${this.mapOptions.zoom}`;

    // color : #RRGGBBAA
    url += '&path=fillcolor:0x3C46DC33|color:0x3C46DC|weight:2';

    const r = 6371;
    const rad = 450;

    const pi = Math.PI;

    const _lat = (this.mapOptions.center.lat * pi) / 180;
    const _lng = (this.mapOptions.center.lng * pi) / 180;
    const d = (rad / 1000) / r;

    for (let i = 0; i <= 360; i += 1) {
      const brng = (i * pi) / 180;

      let pLat = Math.asin(Math.sin(_lat) * Math.cos(d) + Math.cos(_lat) * Math.sin(d) * Math.cos(brng));
      const pLng = ((_lng + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(_lat), Math.cos(d) - Math.sin(_lat) * Math.sin(pLat))) * 180) / pi;
      pLat = (pLat * 180) / pi;

      url += `|${pLat},${pLng}`;
    }

    url += `&key=${this._getApiKey()}`;

    return encodeURI(url);
  }

  /**
   * Render map into DOM element
   */
  render() {
    google.maps.event.addDomListener(window, 'load', () => { this._initMap(); }); // eslint-disable-line no-undef
  }

  /**
   * Render map into modal for example
   */
  renderFull() {
    if (this.el) {
      this._initMap();
    }
  }

  /**
   * Render by adding url to src image
   */
  renderStatic() {
    const img = this.el.querySelector('img.google-map__static-map-button__img--small');
    if (img) {
      img.src = this._getStaticMapUrl();
    }
    const imgLarge = this.el.querySelector('img.google-map__static-map-button__img--large');
    if (imgLarge) {
      imgLarge.src = this._getStaticMapUrl(false);
    }
  }

  /**
   * Toggle transportation network on map
   */
  toggleTransitNetwork() {
    if (this.transitLayer.getMap() == null) {
      this.transitLayer.setMap(this.map);
    } else {
      this.transitLayer.setMap(null);
    }
  }
}
